import {Component, OnInit, OnDestroy, ViewChild}        from '@angular/core';
import {FormBuilder, FormGroup, Validators}             from "@angular/forms";
import {NgbActiveModal}                                 from "@ng-bootstrap/ng-bootstrap";
import {HttpErrorResponse}                              from "@angular/common/http";

import {AngularQuery}                                   from "../../services/query/angularquery";
import {Message}                                        from "../../services/message/message";
import {MessageEntry}                                   from "../../services/message/message-entry";
import {Vendor}                                         from "../../services/vendor/vendor";
import {VendorService}                                  from "../../services/vendor/vendor.service";

import {isNullOrUndefined}                              from "util";
import {Subscription}                                   from "rxjs/Subscription";



@Component({
	selector: 'app-vendor-admin',
	templateUrl: './vendor-admin.component.html',
	styleUrls: ['./vendor-admin.component.css']
})


/**
 * Add, edit or delete Vendor information.
 */
export class VendorAdminComponent implements OnInit, OnDestroy {


	message:Message;
	tForm: FormGroup;
	vendor: Vendor;
	vendorSubscription:Subscription;
	vendorList:Vendor[] = [];


	/**
	 * Initialize the component.
	 *
	 * @param {NgbActiveModal} activeModal     Used to close the page.
	 * @param {FormBuilder} fb                 Used to create the page objects.
	 * @param {VendorService} vendorService    Used when modifying Vendor objects.
	 */
	constructor(
		public activeModal: NgbActiveModal,
		private fb: FormBuilder,
		private vendorService: VendorService,
	) {}



	/**
	 *  Unsubscribe from the Subscriptions to avoid memory leak.
	 */
	ngOnDestroy() {

		if (this.vendorSubscription) {

			this.vendorSubscription.unsubscribe();

		}

	}


	/**
	 * Initialize the FormBuilder, retrieve the existing vendors and send the 'Ready' message.
	 */
	ngOnInit() {

		// Initialize the data structure for the page.
		this.tForm = this.fb.group({
			id: [-1, [Validators.required, Validators.min(-1)]],
			name: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(45),]],
			json_name: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(45),]],
			data_name: ['', [Validators.required,Validators.minLength(1), Validators.maxLength(45),]],
			create_edit_delete: ['edit'],
		});

		// Set up the message window with an initial message.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Loading...';
		me.level = 1;
		this.message = new Message(me);
		this.message.show();

		// Get the list of vendors.
		this.vendorSubscription = this.vendorService.getVendors().subscribe(

			(vendors) => {

				if (Array.isArray(vendors)) {

					// Fill it with the new ones.
					for (let vendor of vendors) {

						this.vendorList.push(new Vendor(vendor));

					}

					me.message = this.vendorList.length + ' existing vendors loaded';
					me.level = 2;
					this.message.add(me, true);
					this.message.show();

					this.tForm.controls['id'].setValue(this.vendorList[0].id);
					this.tForm.controls['name'].setValue(this.vendorList[0].name);
					this.tForm.controls['json_name'].setValue(this.vendorList[0].json_name);
					this.tForm.controls['data_name'].setValue(this.vendorList[0].data_name);

					return;

				}

				me.message = 'Existing vendor data did not load properly';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			},

			() => {

				me.message = 'Retrieval of vendor data failed';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);


	}


	/**
	 * Lets me talk to the Vendor select.
	 */
	@ViewChild('vendorSelect')
	vendorSelect: any;



	/**
	 * Fill the fields with default data.
	 */
	clear():void {

		this.tForm.controls['id'].setValue(-1);
		this.tForm.controls['name'].setValue('');
		this.tForm.controls['json_name'].setValue('');
		this.tForm.controls['data_name'].setValue('');

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Field cleared';
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

	}


	/**
	 * Delete the current item.
	 */
	deleteVendor():void {

		let name:string = this.tForm.controls['name'].value;

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = "Deleting '" + name + "' ...";
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

		if (this.vendorSubscription) {

			this.vendorSubscription.unsubscribe();

		}

		this.vendorSubscription = this.vendorService.deleteVendor(this.tForm.controls['id'].value).subscribe(

			() => {

				this.clear();

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = "'" + name + "' deleted";
				me.level = 2;
				this.message.add(me, true);
				this.message.show();

				// Remove the vendor from the list of existing vendors.
				let i:number = this.vendorSelect.nativeElement.selectedIndex;
				this.vendorSelect.nativeElement.selectedIndex = 0;
				this.vendorList.splice(i, 1);
				this.onSelectChange(0);

			},
			() => {

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = "Failed to delete '" + name + "'";
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * This is used by the Create/Edit/Delete radio buttons to
	 * turn the selected value a different color.
	 *
	 * @param name  The value for the radio button.
	 * @returns {any} A style object with content or empty.
	 */
	isCreateEditDeleteHighlighted(name: string): any {

		if (name === this.tForm.controls['create_edit_delete'].value) {

			return {'color': 'blue', 'font-weight': 'bold'};

		}

		return {};

	}


	/**
	 * Handle clicks on the Change/Edit/Delete radio buttons.
	 *
	 * @param {string} choice  The highlighted field value.
	 */
	onCEDChange(choice:string): void {

		this.clear();

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = "'" + choice.substr(0,1).toUpperCase() + choice.substr(1) + " vendor' chosen";
		me.level = 2;
		this.message.add(me, true);
		this.message.show();

		if ('create' === choice) {

			this.tForm.controls['id'].setValue(-1);
			this.tForm.controls['name'].setValue('');
			this.tForm.controls['json_name'].setValue('');
			this.tForm.controls['data_name'].setValue('');

		} else {

			let i:number = this.vendorSelect.nativeElement.selectedIndex;
			this.tForm.controls['id'].setValue(this.vendorList[i].id);
			this.tForm.controls['name'].setValue(this.vendorList[i].name);
			this.tForm.controls['json_name'].setValue(this.vendorList[i].json_name);
			this.tForm.controls['data_name'].setValue(this.vendorList[i].data_name);

		}

	}


	/**
	 * Target for the 'Save' button.  This adds the new vendor to the vendor list or saves changes to
	 * an existing vendor and fills in the fields on the parent page.
	 */
	saveVendor():void {

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = "Saving '" + this.tForm.controls['name'].value + "' ...";
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

		// Create the JSON object for post.
		let json:object = {};
		json['id'] = ( 0 >= this.tForm.controls['id'].value ) ? null : this.tForm.controls['id'].value;
		json['name'] = this.tForm.controls['name'].value;
		json['json_name'] = this.tForm.controls['json_name'].value;
		json['data_name'] = this.tForm.controls['data_name'].value;

		// Create the post query.
		let query:AngularQuery = new AngularQuery();
		query.useCacheBuster(false);
		query.useParams(false);

		if ( 0 >= this.tForm.controls['id'].value ) {

			query.setEndpoint( AngularQuery.ENDPOINT_VENDOR, 'new' );

		} else {

			query.setEndpoint( AngularQuery.ENDPOINT_VENDOR, this.tForm.controls['id'].value.toString() );

		}

		// Unsubscribe from any existing query.
		if (this.vendorSubscription) {

			this.vendorSubscription.unsubscribe();

		}

		// Save the tag and get back the updated data.
		this.vendorSubscription = this.vendorService.save(query.getUrl(), JSON.stringify(json)).subscribe(

			(response) => {

				let v:Vendor = new Vendor(response);

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = "'" + v.name + "' saved";
				me.level = 2;
				this.message.add(me, true);
				this.message.show();

				this.onVendorChange(v);

			},

			(e_response) => {

				// Notify the user that things failed.
				let me:MessageEntry = new MessageEntry();

				if (e_response instanceof HttpErrorResponse && ! isNullOrUndefined(e_response.error.message)) {

					me.message = 'Failed to save changes. Message: ' + e_response.error.message;

				} else {

					me.message = 'Failed to save changes';

				}

				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Change the form variables when the SELECT object
	 * changes.
	 *
	 * @param {number} index  The vendorList[index] value of the newly selected item.
	 */
	onSelectChange(index:number):void {

		let vendor:Vendor = this.vendorList[index];
		this.tForm.controls['id'].setValue(vendor.id);
		this.tForm.controls['name'].setValue(vendor.name);
		this.tForm.controls['json_name'].setValue(vendor.json_name);
		this.tForm.controls['data_name'].setValue(vendor.data_name);

	}


	/**
	 * Update everything when a vendor's information changes or is added.
	 *
	 * @param {Vendor} vendor  The new Vendor object.
	 */
	onVendorChange(vendor: Vendor): void {

		if ( 0 >= this.tForm.controls['id'].value ) {

			this.vendorList.push(vendor);
			this.tForm.controls['id'].setValue(vendor.id);

		} else {

			// Find the index of the existing item.
			let index:number = -1;

			for (let key in this.vendorList) {

				if (vendor.id === this.vendorList[key].id) {

					index = parseInt(key);
					break;

				}

			}

			if (-1 === index) {

				let me:MessageEntry = new MessageEntry();
				me.message = "Unable to find '" + vendor.name + "' in existing vendor list";
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			} else {

				this.vendorList[index].name = vendor.name;
				this.vendorList[index].json_name = vendor.json_name;
				this.vendorList[index].data_name = vendor.data_name;

			}

		}

	}


}



// WEBPACK FOOTER //
// ./src/app/admin/vendor/vendor-admin.component.ts